import React from "react";
import {
  ValuesList,
  Title,
  LoadingText,
  PreviewTitle,
  PreviewSubtitle,
  CardList,
} from "./styles";
import { CircularProgress } from "@mui/material";
import { Button } from "../common/button";
import { useTranslation } from "react-i18next";
import { CsvUploadResponse } from "../../types/Csvtypes";

interface Props {
  fileName?: string;
  summary?: CsvUploadResponse["data"];
  loading?: boolean;
  fileLoadError?: boolean | string;
  onStartUpload: () => void;
}

const CsvDataPreview: React.FC<Props> = ({
  fileName,
  summary,
  loading = false,
  fileLoadError = false,
  onStartUpload,
}) => {
  const { t } = useTranslation();

  return (
    <ValuesList loading={loading}>
      {/* 1. Empty state */}
      {!loading && !summary && !fileLoadError && (
        <CardList center>
          <PreviewTitle>{fileName}</PreviewTitle>
          <PreviewSubtitle>{t("PREVIEW_MESSAGE")}</PreviewSubtitle>
          <Button narrow label={t("UPLOAD")} onClick={onStartUpload} />
        </CardList>
      )}

      {/* 2. Error */}
      {!loading && fileLoadError && (
        <CardList center>
          <PreviewTitle>{fileName}</PreviewTitle>
          <PreviewTitle>
            {fileLoadError === "RATE_LIMIT"
              ? t("PLEASE_TRY_AGAIN_LATER") // <-- translation or fallback
              : t("FILE_LOADING_ERROR")}
          </PreviewTitle>
          <Button narrow label={t("UPLOAD")} onClick={onStartUpload} />
        </CardList>
      )}

      {/* 3. Success – show summary */}
      {!loading && summary && (
        <CardList style={{ width: "70vw" }}>
          <Title>{fileName}</Title>
          <PreviewSubtitle>
            {t("ROUTING_RULES_PROCESSED", {
              count: summary.routing_rules_processed,
              defaultValue: `${summary.routing_rules_processed} routing rules processed`,
            })}
          </PreviewSubtitle>
          <PreviewSubtitle>
            {t("SUPPLIERS_PROCESSED", {
              count: summary.suppliers_processed,
              defaultValue: `${summary.suppliers_processed} suppliers processed`,
            })}
          </PreviewSubtitle>
          {summary.errors.length > 0 && (
            <>
              <PreviewTitle>{t("ERRORS")}</PreviewTitle>
              <ul>
                {summary.errors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            </>
          )}
        </CardList>
      )}

      {/* 4. Spinner */}
      {loading && (
        <>
          <CircularProgress />
          <LoadingText>{t("LOADING_DATA")}</LoadingText>
        </>
      )}
    </ValuesList>
  );
};

export default CsvDataPreview;
